import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const achievements = [
  { year: "2026", text: "入选年度杰出青年代表，并受邀在品牌影响力峰会发表主题演讲" },
  { year: "2025", text: "发起“微光计划”公益影像项目，首支短片《微光》上线获广泛关注" },
  { year: "2024", text: "完成时尚杂志封面企划《清醒》，主视觉成为当年讨论度最高的封面之一" },
  { year: "2023", text: "推出首个品牌联名视觉企划《相遇》，探索个人品牌的商业合作边界" },
  { year: "2022", text: "完成个人首部纪录短片《一年》，正式开启影像创作方向" },
];

const philosophy = [
  {
    icon: "ri-focus-3-line",
    title: "真诚表达",
    desc: "不迎合、不夸张，把每一句话、每一帧画面都建立在真实之上。",
  },
  {
    icon: "ri-lightbulb-line",
    title: "持续创造",
    desc: "把每一次表达都当作作品打磨，在稳定输出中不断进化。",
  },
  {
    icon: "ri-heart-3-line",
    title: "保留温度",
    desc: "专业的背后始终保留对人的关怀，让内容有力量也有温度。",
  },
  {
    icon: "ri-compasses-2-line",
    title: "长期主义",
    desc: "不追逐短期热度，只做经得起时间检验的选择。",
  },
];

const partners = [
  "微光公益基金会",
  "城市之光影像工作室",
  "相遇设计事务所",
  "在场文化传媒",
  "山海出版",
  "启明青年计划",
];

const directions = [
  "持续深耕公益影像，把“微光计划”带到更多地区",
  "推出个人首部纪录长片，用影像记录普通人的闪光日常",
  "开放更多内容共创与品牌联名合作，探索个人品牌的长期价值",
];

export default function About() {
  usePageMeta({
    title: "关于张津瑜 - 个人品牌官方网站",
    description:
      "了解张津瑜的个人简介、成就、品牌理念与发展方向，以及合作伙伴与网站声明。",
    keywords: "关于张津瑜,张津瑜简介,品牌理念,合作伙伴",
  });

  return (
    <>
      <section className="bg-primary-800 py-10 md:py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-400 [&_span]:text-background-100">
            <Breadcrumb items={[{ label: "关于张津瑜" }]} />
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
            关于张津瑜
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300 md:text-base">
            一名内容创作者，也是个人品牌的经营者。始终相信真诚的表达，可以被时间反复验证。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div className="h-[380px] w-full overflow-hidden rounded-lg md:h-[520px]">
              <img
                src="https://readdy.ai/api/search-image?query=Refined%20minimal%20editorial%20portrait%20of%20a%20thoughtful%20creative%20professional%20in%20soft%20warm%20studio%20lighting%2C%20deep%20navy%20backdrop%20with%20subtle%20orange%20accent%2C%20premium%20brand%20photography%20high%20detail&width=1000&height=1200&seq=about-portrait-01&orientation=portrait"
                alt="张津瑜个人形象照 - 关于页面"
                title="张津瑜 + 个人品牌形象"
                className="h-full w-full object-cover object-top"
              />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-accent-500" />
                Profile
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                个人简介
              </h2>
              <div className="mt-5 space-y-4 text-sm leading-relaxed text-foreground-700 md:text-base">
                <p>
                  张津瑜，内容创作者、影像制作人、个人品牌经营者。五年来，她持续在内容创作与影像表达之间探索，
                  用镜头与文字记录城市、乡村与普通人身上那些容易被忽略的瞬间。
                </p>
                <p>
                  她的作品涵盖视觉大片、品牌短片、公益影像与人物访谈，风格沉稳克制，擅长在光影对比中传递温度。
                  对她而言，创作不是表演，而是一种持续的自我确认。
                </p>
                <p>
                  “先成为一个完整的人，再成为一个创作者。”这是她常常提起的一句话，也是她所有作品背后的底色。
                </p>
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  查看作品
                  <i className="ri-arrow-right-line" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  联系合作
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-accent-500" />
                Achievements
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                成就简介
              </h2>
              <ol className="mt-7 space-y-6 border-l border-background-300 pl-6">
                {achievements.map((item) => (
                  <li key={item.year} className="relative">
                    <span className="absolute -left-[31px] top-1 h-3 w-3 rounded-full border-2 border-background-100 bg-accent-500" />
                    <span className="font-label text-sm font-bold text-primary-600">
                      {item.year}
                    </span>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-700">
                      {item.text}
                    </p>
                  </li>
                ))}
              </ol>
            </div>

            <div>
              <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
                <span className="h-px w-6 bg-accent-500" />
                Philosophy
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
                品牌理念
              </h2>
              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {philosophy.map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-5"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-50 text-primary-600">
                      <i className={`${item.icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                      {item.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-14 rounded-lg bg-primary-900 p-8 md:p-10">
            <h2 className="font-heading text-xl font-bold text-background-50 md:text-2xl">
              发展方向
            </h2>
            <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
              {directions.map((dir) => (
                <li key={dir} className="flex gap-3">
                  <i className="ri-arrow-right-circle-line mt-0.5 text-xl text-accent-500" />
                  <span className="text-sm leading-relaxed text-background-200">{dir}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-6 lg:px-10">
          <span className="inline-flex items-center gap-2 font-label text-xs font-semibold uppercase tracking-[0.2em] text-accent-600">
            <span className="h-px w-6 bg-accent-500" />
            Partners
          </span>
          <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            合作伙伴
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            感谢以下机构与团队在内容共创、影像制作与公益项目中的长期支持（排名不分先后）。
          </p>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {partners.map((name) => (
              <div
                key={name}
                className="flex h-20 items-center justify-center rounded-lg border border-background-200 bg-background-100 px-3 text-center text-sm font-medium text-foreground-700"
              >
                {name}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto max-w-4xl px-4 md:px-6 lg:px-10">
          <h2 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            网站声明及使用条款
          </h2>
          <div className="mt-6 space-y-5 text-sm leading-relaxed text-foreground-700">
            <p>
              本网站为张津瑜个人品牌官方展示平台，站内资讯、作品及图文内容除特别标注外，均为品牌方原创或经授权使用，
              版权归张津瑜个人品牌所有。
            </p>
            <p>
              未经书面许可，任何机构或个人不得对本站内容进行转载、改编或用于商业用途。获得授权后转载的，
              须完整保留作者署名与来源信息，不得断章取义或作误导性修改。
            </p>
            <p>
              本站部分栏目（如数据统计、留言表单等）当前为前端演示内容，尚未接入真实业务接口，
              相关展示数据仅用于功能演示，不代表最终业务结果。
            </p>
            <p>
              如需内容授权、商业合作或对本站内容有任何疑问，欢迎通过
              <Link to="/contact" className="mx-1 font-medium text-primary-600 underline">
                联系我们
              </Link>
              页面提交申请，我们将在三个工作日内回复。
            </p>
          </div>
        </div>
      </section>
    </>
  );
}